import { create } from 'zustand'
import { z } from 'zod'
import type {
  ArchiveRecord,
  BehaviorAction,
  BehaviorEvent,
  ChannelName,
  GenerationChannel,
  InputMode,
  Phase,
  Region,
  VisitSnapshot,
} from '../shared/types'
import { DURATION_MS, EXHIBITS, MAPPING_VERSION, SCENE_VERSION, getExhibit } from '../content/exhibits'
import { emptyGenerationChannel, isCurrentAttempt, localSummary, mockGenerationAdapter } from './generation'
import { EXHIBIT_PREVIEW_MS, EXHIBIT_PREVIEW_PUBLISH_INTERVAL_MS } from './proximity'
import { loadAutoEnterExhibits, saveAutoEnterExhibits } from './preferences'
import { freezeSnapshot, getLatestDraft, listArchives, saveArchive, saveDraft, updateArchive, type VisitDraft } from './storage'

const ACTIONS: BehaviorAction[] = ['browse', 'play', 'agree', 'oppose', 'ignore', 'comment', 'share', 'grow', 'report']
const actionSchema = z.enum(['browse', 'play', 'agree', 'oppose', 'ignore', 'comment', 'share', 'grow', 'report'])
const regionSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)])
const eventSchema = z.object({
  eventId: z.string().min(1).max(80),
  sequence: z.number().int().min(1).max(500),
  elapsedMs: z.number().finite().min(0).max(DURATION_MS),
  region: regionSchema,
  exhibitId: z.string().min(1).max(80),
  triggerId: z.string().min(1).max(100),
  action: actionSchema,
  sourceRowId: z.string().min(1).max(100),
  mappingVersion: z.string().min(1).max(64),
  termIds: z.array(z.string().min(1).max(80)).max(24),
  visualTerms: z.array(z.string().min(1).max(100)).max(24),
  detail: z.string().max(240).optional(),
})
function validateEventList(events: BehaviorEvent[], ctx: z.RefinementCtx, activeDurationMs: number): void {
  const eventIds = new Set<string>()
  const onceActions = new Set<string>()
  let previousElapsed = 0
  events.forEach((event, index) => {
    const exhibit = getExhibit(event.exhibitId)
    if (!eventMatchesContent(event)) ctx.addIssue({ code: 'custom', path: ['events', index], message: '互动记录与展品内容不匹配。' })
    if (event.sequence !== index + 1) ctx.addIssue({ code: 'custom', path: ['events', index, 'sequence'], message: '互动序号必须连续。' })
    if (eventIds.has(event.eventId)) ctx.addIssue({ code: 'custom', path: ['events', index, 'eventId'], message: '互动 ID 重复。' })
    eventIds.add(event.eventId)
    if (event.elapsedMs < previousElapsed || event.elapsedMs > activeDurationMs + 1) ctx.addIssue({ code: 'custom', path: ['events', index, 'elapsedMs'], message: '互动时间必须有序且不晚于参观时长。' })
    previousElapsed = event.elapsedMs
    if (event.action === 'comment' && !event.detail?.trim()) ctx.addIssue({ code: 'custom', path: ['events', index, 'detail'], message: '评论内容不能为空。' })
    const dedupeKey = `${event.exhibitId}:explicit`
    if (event.action !== 'browse' && exhibit?.actionPolicy === 'once' && onceActions.has(dedupeKey)) ctx.addIssue({ code: 'custom', path: ['events', index], message: '该展品只能提交一次显式选择。' })
    if (event.action !== 'browse' && exhibit?.actionPolicy === 'once') onceActions.add(dedupeKey)
  })
}
const snapshotSchema = z.object({
  schemaVersion: z.literal('humanmuseum.visit.v1'),
  visitId: z.string().min(1).max(80),
  visitorLabel: z.string().min(1).max(48),
  sceneVersion: z.string().min(1).max(80),
  mappingVersion: z.string().min(1).max(64),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime(),
  endReason: z.enum(['time_limit', 'visitor_finished']),
  activeDurationMs: z.number().finite().min(0).max(DURATION_MS),
  events: z.array(eventSchema).max(500),
}).superRefine((snapshot, ctx) => validateEventList(snapshot.events as BehaviorEvent[], ctx, snapshot.activeDurationMs))
const generationChannelSchema = z.object({
  status: z.enum(['idle', 'running', 'succeeded', 'failed']),
  attempt: z.number().int().min(0).max(10_000),
  result: z.string().max(2_000_000).optional(),
  error: z.string().max(500).optional(),
})
const archiveSchema = z.object({
  snapshot: snapshotSchema,
  summary: z.string().max(100_000),
  provider: z.literal('mock'),
  text: generationChannelSchema,
  image: generationChannelSchema,
})
const draftSchema = z.object({
  visitId: z.string().min(1).max(80),
  visitorLabel: z.string().min(1).max(48),
  startedAt: z.string().datetime(),
  activeDurationMs: z.number().finite().min(0).max(DURATION_MS),
  remainingMs: z.number().finite().min(0).max(DURATION_MS),
  events: z.array(eventSchema).max(500),
}).superRefine((draft, ctx) => validateEventList(draft.events as BehaviorEvent[], ctx, draft.activeDurationMs))

export type PersistenceStatus = 'idle' | 'saving' | 'saved' | 'error'

interface VisitState {
  phase: Phase
  visitorLabel: string
  remainingMs: number
  previewRemainingMs: number | null
  events: BehaviorEvent[]
  selectedExhibitId: string | null
  nearbyExhibitId: string | null
  archives: ArchiveRecord[]
  selectedArchiveId: string | null
  persistenceStatus: PersistenceStatus
  error: string | null
  notice: string | null
  resetToken: number
  inputMode: InputMode
  isFocused: boolean
  autoEnterExhibits: boolean
  setFocused(focused: boolean): void
  hydrate(): Promise<void>
  start(label: string): void
  begin(): void
  pause(): void
  resume(): void
  openExhibit(id: string): void
  closeExhibit(): void
  record(action: BehaviorAction, detail?: string): boolean
  finish(reason?: 'time_limit' | 'visitor_finished'): void
  setNearby(id: string | null): void
  cancelPreview(): void
  tick(deltaMs: number): void
  returnToEntrance(): void
  reset(): void
  selectArchive(id: string): void
  retryGeneration(id: string, channel: ChannelName): void
  setInputMode(mode: InputMode): void
  setAutoEnterExhibits(enabled: boolean): void
}

const blankState = () => ({
  phase: 'welcome' as Phase,
  visitorLabel: '',
  remainingMs: DURATION_MS,
  previewRemainingMs: null as number | null,
  events: [] as BehaviorEvent[],
  selectedExhibitId: null as string | null,
  nearbyExhibitId: null as string | null,
  selectedArchiveId: null as string | null,
  persistenceStatus: 'idle' as PersistenceStatus,
  error: null as string | null,
  notice: null as string | null,
  resetToken: 0,
  inputMode: 'pointer' as InputMode,
  isFocused: true,
  autoEnterExhibits: false,
})

let currentVisitId: string | null = null
let visitStartedAt = ''
let activeElapsedMs = 0
let exactRemainingMs = DURATION_MS
let lastPublishedSecond = Math.ceil(DURATION_MS / 1000)
let exactPreviewRemainingMs: number | null = null
let hydrationPromise: Promise<void> | undefined
let storageWriteQueue = Promise.resolve()
const generationControllers = new Map<string, AbortController>()

function makeId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `visit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

function clearPreview(): void {
  exactPreviewRemainingMs = null
}

function publishedPreviewRemaining(remainingMs: number): number {
  return remainingMs <= 0 ? 0 : Math.ceil(remainingMs / EXHIBIT_PREVIEW_PUBLISH_INTERVAL_MS) * EXHIBIT_PREVIEW_PUBLISH_INTERVAL_MS
}

function freezeEvent(event: BehaviorEvent): BehaviorEvent {
  return Object.freeze({ ...event, termIds: Object.freeze([...event.termIds]) as unknown as string[], visualTerms: Object.freeze([...event.visualTerms]) as unknown as string[] })
}

function eventMatchesContent(event: BehaviorEvent): boolean {
  const exhibit = getExhibit(event.exhibitId)
  return Boolean(exhibit && exhibit.region === event.region && (event.action === 'browse' || exhibit.actions.includes(event.action)))
}

function queueStorageWrite(operation: () => Promise<void>): Promise<void> {
  const result = storageWriteQueue.then(operation, operation)
  storageWriteQueue = result.catch(() => undefined)
  return result
}

function persistArchiveUpdate(archive: ArchiveRecord): void {
  const visitId = archive.snapshot.visitId
  void queueStorageWrite(() => updateArchive(archive)).catch((error: unknown) => {
    const message = error instanceof Error ? error.message : '档案更新未保存。'
    useVisitStore.setState((state) => state.selectedArchiveId === visitId
      ? { persistenceStatus: 'error', error: message, notice: '本地档案更新未保存；当前页面仍可查看或导出。' }
      : state)
  })
}

function toDraft(state: Pick<VisitState, 'visitorLabel' | 'events'>): VisitDraft | undefined {
  if (!currentVisitId || !visitStartedAt) return undefined
  return {
    visitId: currentVisitId,
    visitorLabel: state.visitorLabel,
    startedAt: visitStartedAt,
    activeDurationMs: activeElapsedMs,
    remainingMs: exactRemainingMs,
    events: [...state.events],
  }
}

function queueDraftSave(draft: VisitDraft, visitId: string): void {
  useVisitStore.setState({ persistenceStatus: 'saving', error: null })
  void queueStorageWrite(() => saveDraft(draft)).then(() => {
    if (currentVisitId === visitId) useVisitStore.setState({ persistenceStatus: 'saved' })
  }).catch((error: unknown) => {
    if (currentVisitId === visitId) useVisitStore.setState({ persistenceStatus: 'error', error: error instanceof Error ? error.message : '本地草稿保存失败。' })
  })
}

function buildArchive(snapshot: VisitSnapshot): ArchiveRecord {
  return {
    snapshot,
    summary: localSummary(snapshot),
    provider: 'mock',
    text: emptyGenerationChannel(),
    image: emptyGenerationChannel(),
  }
}

function persistArchive(archive: ArchiveRecord): void {
  const visitId = archive.snapshot.visitId
  useVisitStore.setState({ persistenceStatus: 'saving', error: null })
  void queueStorageWrite(() => saveArchive(archive)).then(() => {
    useVisitStore.setState((state) => state.selectedArchiveId === visitId ? { persistenceStatus: 'saved' } : state)
  }).catch((error: unknown) => {
    const message = error instanceof Error ? error.message : '参观档案保存失败。'
    useVisitStore.setState((state) => state.selectedArchiveId === visitId ? { persistenceStatus: 'error', error: message, notice: '本地档案未保存；当前页面仍保留这份摘要，可在离开前导出。' } : state)
  })
}

function validArchive(input: unknown): ArchiveRecord | undefined {
  const parsed = archiveSchema.safeParse(input)
  if (!parsed.success) return undefined
  if (parsed.data.snapshot.events.some((event) => !eventMatchesContent(event))) return undefined
  return { ...parsed.data, snapshot: freezeSnapshot(parsed.data.snapshot as VisitSnapshot) } as ArchiveRecord
}

export const useVisitStore = create<VisitState>((set, get) => ({
  ...blankState(),
  autoEnterExhibits: loadAutoEnterExhibits(),
  archives: [],

  setFocused(focused) {
    set({ isFocused: focused })
    if (!focused) get().pause()
  },

  async hydrate() {
    if (hydrationPromise) return hydrationPromise
    hydrationPromise = (async () => {
      try {
        const [storedArchives, storedDraft] = await Promise.all([listArchives(), getLatestDraft()])
        const archives = storedArchives.map(validArchive).filter((archive): archive is ArchiveRecord => Boolean(archive)).map((archive) => {
          const interrupted: ArchiveRecord = { ...archive }
          for (const channel of ['text', 'image'] as const) {
            if (interrupted[channel].status === 'running') interrupted[channel] = {
              status: 'failed', attempt: interrupted[channel].attempt, error: '页面刷新中断了本地生成，可重试。',
            }
          }
          if (interrupted.text !== archive.text || interrupted.image !== archive.image) persistArchiveUpdate(interrupted)
          return interrupted
        })
        const draftResult = storedDraft ? draftSchema.safeParse(storedDraft) : undefined
        set({ archives, selectedArchiveId: archives[0]?.snapshot.visitId ?? null, persistenceStatus: 'saved', error: null })
        if (storedDraft && draftResult?.success) {
          const draft = draftResult.data
          const validEvents = draft.events.map(freezeEvent)
          currentVisitId = draft.visitId
          visitStartedAt = draft.startedAt
          activeElapsedMs = draft.activeDurationMs
          exactRemainingMs = draft.remainingMs
          lastPublishedSecond = Math.ceil(exactRemainingMs / 1000)
          set({
            phase: 'paused', visitorLabel: draft.visitorLabel, events: validEvents,
            remainingMs: exactRemainingMs, notice: '已恢复未完成的本地参观草稿。',
          })
        } else if (storedDraft && !draftResult?.success) {
          set({ persistenceStatus: 'error', error: '发现无法读取的草稿，已跳过该记录。' })
        }
      } catch (error) {
        set({ persistenceStatus: 'error', error: error instanceof Error ? error.message : '无法读取本地档案。' })
      }
    })()
    return hydrationPromise
  },

  start(label) {
    const visitorLabel = z.string().trim().min(1, '请填写称呼。').max(48, '称呼最多 48 个字符。').safeParse(label)
    if (!visitorLabel.success) { set({ error: visitorLabel.error.issues[0]?.message ?? '称呼无效。' }); return }
    if (get().phase !== 'welcome' && get().phase !== 'archive') return
    currentVisitId = makeId()
    visitStartedAt = new Date().toISOString()
    activeElapsedMs = 0
    exactRemainingMs = DURATION_MS
    lastPublishedSecond = Math.ceil(DURATION_MS / 1000)
    clearPreview()
    set({
      phase: 'guide', visitorLabel: visitorLabel.data, remainingMs: DURATION_MS,
      previewRemainingMs: null,
      events: [], selectedExhibitId: null, nearbyExhibitId: null, selectedArchiveId: null,
      persistenceStatus: 'idle', error: null, notice: null,
    })
    const draft = toDraft(get())
    if (draft) queueDraftSave(draft, currentVisitId)
  },

  begin() {
    if (get().phase !== 'guide') return
    clearPreview()
    set({ phase: 'exploring', nearbyExhibitId: null, previewRemainingMs: null, error: null, notice: '参观开始，计时 90 秒。' })
  },

  pause() {
    if (get().phase !== 'exploring') return
    const previewRemainingMs = exactPreviewRemainingMs === null ? null : EXHIBIT_PREVIEW_MS
    exactPreviewRemainingMs = previewRemainingMs
    set({ phase: 'paused', selectedExhibitId: null, previewRemainingMs, notice: '参观已暂停。' })
    const draft = toDraft(get())
    if (draft) queueDraftSave(draft, draft.visitId)
  },

  resume() {
    if (get().phase !== 'paused' || !get().isFocused) return
    set({ phase: 'exploring', notice: null })
  },

  openExhibit(id) {
    const exhibit = getExhibit(id)
    if (get().phase !== 'exploring' || !get().isFocused || !exhibit || get().nearbyExhibitId !== id) return
    clearPreview()
    set({ selectedExhibitId: id, previewRemainingMs: null, notice: null })
  },

  closeExhibit() {
    if (!get().selectedExhibitId) return
    set({ selectedExhibitId: null })
  },

  record(action, detail) {
    const state = get()
    if (state.phase !== 'exploring' || !state.isFocused || state.selectedExhibitId === null || !currentVisitId) return false
    const exhibit = getExhibit(state.selectedExhibitId)
    const parsedAction = actionSchema.safeParse(action)
    const parsedDetail = detail === undefined ? undefined : z.string().max(240).safeParse(detail)
    if (!exhibit || !parsedAction.success || !exhibit.actions.includes(parsedAction.data) || (parsedDetail && !parsedDetail.success)) return false
    if (action === 'comment' && !detail?.trim()) { set({ error: '回应内容不能为空。' }); return false }
    const eventKey = `${exhibit.id}:${action}`
    if (action === 'browse' && state.events.some((event) => event.exhibitId === exhibit.id && event.action === 'browse')) return false
    if (action !== 'browse' && exhibit.actionPolicy === 'once' && state.events.some((event) => event.exhibitId === exhibit.id && event.action !== 'browse')) return false
    if (state.events.length >= 500) { set({ error: '本次参观记录已达上限。' }); return false }
    const event: BehaviorEvent = freezeEvent({
      eventId: makeId(), sequence: state.events.length + 1,
      elapsedMs: Math.min(DURATION_MS, Math.max(0, activeElapsedMs)),
      region: exhibit.region, exhibitId: exhibit.id,
      triggerId: `${exhibit.id}:${action}`, action: parsedAction.data,
      sourceRowId: exhibit.sourceRowId, mappingVersion: MAPPING_VERSION,
      termIds: [...exhibit.termIds], visualTerms: [...exhibit.visualTerms],
      ...(parsedDetail?.success ? { detail: parsedDetail.data.trim().slice(0, 240) } : {}),
    })
    const checked = eventSchema.safeParse(event)
    if (!checked.success) { set({ error: '互动记录未通过字段校验。' }); return false }
    const events = [...state.events, event]
    set({ events, notice: '互动已记入本地草稿。', error: null })
    const draft = toDraft({ visitorLabel: state.visitorLabel, events })
    if (draft) queueDraftSave(draft, draft.visitId)
    return true
  },

  finish(reason = 'visitor_finished') {
    const state = get()
    if (!['exploring', 'paused'].includes(state.phase) || !currentVisitId) return
    const endReason = reason === 'time_limit' ? 'time_limit' : 'visitor_finished'
    const snapshot = freezeSnapshot({
      schemaVersion: 'humanmuseum.visit.v1', visitId: currentVisitId,
      visitorLabel: state.visitorLabel, sceneVersion: SCENE_VERSION,
      mappingVersion: MAPPING_VERSION, startedAt: visitStartedAt,
      endedAt: new Date().toISOString(), endReason,
      activeDurationMs: Math.min(DURATION_MS, Math.max(0, activeElapsedMs)),
      events: [...state.events],
    })
    const checked = snapshotSchema.safeParse(snapshot)
    if (!checked.success) { set({ persistenceStatus: 'error', error: '档案未通过字段校验，无法封存。' }); return }
    const archive = buildArchive(snapshot)
    clearPreview()
    set((current) => ({
      phase: 'archive', archives: [archive, ...current.archives.filter((item) => item.snapshot.visitId !== snapshot.visitId)],
      selectedArchiveId: snapshot.visitId, selectedExhibitId: null, nearbyExhibitId: null, previewRemainingMs: null,
      notice: '参观已封存；本地摘要可立即查看。', error: null,
    }))
    persistArchive(archive)
    currentVisitId = null
    void get().retryGeneration(snapshot.visitId, 'text')
    void get().retryGeneration(snapshot.visitId, 'image')
  },

  setNearby(id) {
    const state = get()
    const exhibit = id ? getExhibit(id) : undefined
    const nearbyExhibitId = exhibit ? exhibit.id : null
    if (state.nearbyExhibitId === nearbyExhibitId) return
    const canPreview = Boolean(state.autoEnterExhibits && exhibit && state.phase === 'exploring' && state.isFocused && !state.selectedExhibitId && currentVisitId)
    exactPreviewRemainingMs = canPreview ? EXHIBIT_PREVIEW_MS : null
    set({ nearbyExhibitId, previewRemainingMs: canPreview ? EXHIBIT_PREVIEW_MS : null })
    if (exhibit && state.phase === 'exploring' && state.isFocused && !state.selectedExhibitId && currentVisitId) {
      if (!exhibit.actions.includes('browse') || state.events.length >= 500 || state.events.some((event) => event.exhibitId === exhibit.id && event.action === 'browse')) return
      const event = freezeEvent(eventSchema.parse({
        eventId: makeId(), sequence: state.events.length + 1, elapsedMs: activeElapsedMs,
        region: exhibit.region, exhibitId: exhibit.id, triggerId: `${exhibit.id}:browse`,
        action: 'browse', sourceRowId: exhibit.sourceRowId, mappingVersion: MAPPING_VERSION,
        termIds: [...exhibit.termIds], visualTerms: [...exhibit.visualTerms],
      }))
      const events = [...state.events, event]
      set({ events, notice: '驻足浏览已记入本地草稿。', error: null })
      const draft = toDraft({ visitorLabel: state.visitorLabel, events })
      if (draft) queueDraftSave(draft, draft.visitId)
    }
  },

  cancelPreview() {
    if (exactPreviewRemainingMs === null && get().previewRemainingMs === null) return
    clearPreview()
    set({ previewRemainingMs: null })
  },

  tick(deltaMs) {
    const state = get()
    if (state.phase !== 'exploring' || !state.isFocused || state.selectedExhibitId || !Number.isFinite(deltaMs) || deltaMs <= 0) return
    const step = Math.min(deltaMs, Math.max(0, DURATION_MS - activeElapsedMs))
    activeElapsedMs = Math.min(DURATION_MS, activeElapsedMs + step)
    exactRemainingMs = Math.max(0, DURATION_MS - activeElapsedMs)
    const second = Math.ceil(exactRemainingMs / 1000)
    const update: Partial<VisitState> = {}
    if (second !== lastPublishedSecond) {
      lastPublishedSecond = second
      update.remainingMs = exactRemainingMs
    }
    if (exactRemainingMs <= 0) {
      if (Object.keys(update).length) set(update)
      get().finish('time_limit')
      return
    }

    if (state.autoEnterExhibits && exactPreviewRemainingMs !== null) {
      exactPreviewRemainingMs = Math.max(0, exactPreviewRemainingMs - step)
      const previewRemainingMs = publishedPreviewRemaining(exactPreviewRemainingMs)
      if (exactPreviewRemainingMs <= 0) {
        const target = get().nearbyExhibitId
        if (target && getExhibit(target) && !get().selectedExhibitId) {
          update.selectedExhibitId = target
          update.notice = null
        }
        clearPreview()
        update.previewRemainingMs = null
      } else if (previewRemainingMs !== state.previewRemainingMs) {
        update.previewRemainingMs = previewRemainingMs
      }
    }
    if (Object.keys(update).length) set(update)
  },

  returnToEntrance() {
    const phase = get().phase
    if (phase === 'archive') { clearPreview(); set({ phase: 'welcome', visitorLabel: '', events: [], remainingMs: DURATION_MS, nearbyExhibitId: null, selectedExhibitId: null, previewRemainingMs: null, selectedArchiveId: null }); currentVisitId = null; return }
    if (phase !== 'exploring' && phase !== 'paused') return
    clearPreview()
    set((state) => ({ resetToken: state.resetToken + 1, nearbyExhibitId: null, selectedExhibitId: null, previewRemainingMs: null, notice: '已返回入口，计时继续。' }))
  },

  reset() {
    for (const controller of generationControllers.values()) controller.abort()
    generationControllers.clear()
    const currentArchives = get().archives.map((archive) => {
      const updated: ArchiveRecord = { ...archive }
      for (const channel of ['text', 'image'] as const) {
        if (updated[channel].status === 'running') updated[channel] = {
          status: 'failed', attempt: updated[channel].attempt, error: '本地生成已取消，可重试。',
        }
      }
      if (updated.text !== archive.text || updated.image !== archive.image) persistArchiveUpdate(updated)
      return updated
    })
    currentVisitId = null
    visitStartedAt = ''
    activeElapsedMs = 0
    exactRemainingMs = DURATION_MS
    lastPublishedSecond = Math.ceil(DURATION_MS / 1000)
    clearPreview()
    set((state) => ({ ...blankState(), autoEnterExhibits: state.autoEnterExhibits, archives: currentArchives, resetToken: state.resetToken + 1 }))
  },

  selectArchive(id) {
    if (!get().archives.some((archive) => archive.snapshot.visitId === id)) return
    set({ phase: 'archive', selectedArchiveId: id })
  },

  retryGeneration(id, channel) {
    const current = get().archives.find((archive) => archive.snapshot.visitId === id)
    if (!current) return
    const key = `${id}:${channel}`
    generationControllers.get(key)?.abort()
    const controller = new AbortController()
    generationControllers.set(key, controller)
    const attempt = current[channel].attempt + 1
    const runningChannel: GenerationChannel = { status: 'running', attempt }
    const runningArchive: ArchiveRecord = { ...current, [channel]: runningChannel }
    set((state) => ({ archives: state.archives.map((archive) => archive.snapshot.visitId === id ? runningArchive : archive), error: null }))
    persistArchiveUpdate(runningArchive)
    void mockGenerationAdapter.generate(current.snapshot, channel, controller.signal).then((result) => {
      if (controller.signal.aborted) return
      const archive = get().archives.find((item) => item.snapshot.visitId === id)
      if (!isCurrentAttempt(archive, channel, attempt)) return
      const done: ArchiveRecord = { ...archive!, [channel]: { status: 'succeeded', attempt, result } }
      set((state) => ({ archives: state.archives.map((item) => item.snapshot.visitId === id ? done : item) }))
      persistArchiveUpdate(done)
      if (generationControllers.get(key) === controller) generationControllers.delete(key)
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      const archive = get().archives.find((item) => item.snapshot.visitId === id)
      if (!isCurrentAttempt(archive, channel, attempt)) return
      const failed: ArchiveRecord = { ...archive!, [channel]: { status: 'failed', attempt, error: error instanceof Error ? error.message : '本地生成失败。' } }
      set((state) => ({ archives: state.archives.map((item) => item.snapshot.visitId === id ? failed : item) }))
      persistArchiveUpdate(failed)
      if (generationControllers.get(key) === controller) generationControllers.delete(key)
    })
  },

  setInputMode(mode) {
    set({ inputMode: mode })
  },

  setAutoEnterExhibits(enabled) {
    const current = get()
    if (current.autoEnterExhibits === enabled) return
    saveAutoEnterExhibits(enabled)
    if (!enabled) {
      clearPreview()
      set({ autoEnterExhibits: false, previewRemainingMs: null })
      return
    }
    const target = current.nearbyExhibitId ? getExhibit(current.nearbyExhibitId) : undefined
    const canPreview = Boolean(
      target && (current.phase === 'exploring' || current.phase === 'paused') && current.isFocused
      && !current.selectedExhibitId && currentVisitId,
    )
    exactPreviewRemainingMs = canPreview ? EXHIBIT_PREVIEW_MS : null
    set({ autoEnterExhibits: true, previewRemainingMs: canPreview ? EXHIBIT_PREVIEW_MS : null })
  },
}))

export { ACTIONS, eventSchema, snapshotSchema, archiveSchema, draftSchema }
